/**
 * Manages variant selection for gallery artworks
 */
export class GalleryVariantManager {
  constructor() {
    this.variant_cache = new Map();
    this.price_cache = new Map();
    this.selected_config = {
      size: null,
      frame: null,
      mount: null
    };
  }

  /**
   * Unified variant finding with multiple modes
   */
  findVariant(artwork, config) {
    const { size, frame, mount, mode = 'exact' } = config;

    const cache_key = `${artwork.id}-${size}-${frame}-${mount}-${mode}`;

    if (this.variant_cache.has(cache_key)) {
      return this.variant_cache.get(cache_key);
    }

    let variant = null;

    if (mode === 'exact') {
      variant = this._findExactVariant(artwork, size, frame, mount);
    } else if (mode === 'preview') {
      variant = this._findVariantForPreview(artwork, size, frame, mount);
    } else if (mode === 'closest') {
      variant = this._findClosestMatch(artwork, size, frame, mount);
    }

    if (variant) {
      this.variant_cache.set(cache_key, variant);
    }

    return variant;
  }

  _findExactVariant(artwork, size, frame, mount) {
    if (!artwork?.variants) return null;

    return artwork.variants.find(v =>
      v.option1 === size &&
      v.option2 === frame &&
      v.option3 === mount
    ) || null;
  }

  _findVariantForPreview(artwork, size, frame, mount) {
    if (!artwork?.variants) return null;

    const exact = this._findExactVariant(artwork, size, frame, mount);
    if (exact && exact.available && exact.featured_image) {
      return { ...exact, product_id: artwork.id };
    }

    const defaultSize = size || 'Exhibition';
    const defaultFrame = frame || 'Black';
    const defaultMount = mount || 'Border';

    const withDefaults = this._findExactVariant(artwork, defaultSize, defaultFrame, defaultMount);
    if (withDefaults && withDefaults.available && withDefaults.featured_image) {
      return { ...withDefaults, product_id: artwork.id };
    }

    if (size || frame || mount) {
      const partial = artwork.variants.find(v =>
        v.available && v.featured_image &&
        (!size || v.option1 === size) &&
        (!frame || v.option2 === frame) &&
        (!mount || v.option3 === mount)
      );
      if (partial) {
        return { ...partial, product_id: artwork.id, is_preview: true };
      }
    }

    const fallback = artwork.variants.find(v => v.available && v.featured_image);
    if (fallback) {
      return { ...fallback, product_id: artwork.id, is_preview: true };
    }

    return null;
  }

  _findClosestMatch(artwork, size, frame, mount) {
    if (!artwork?.variants) return null;

    if (size) {
      const sameSize = artwork.variants.filter(v => v.option1 === size && v.available);
      if (sameSize.length > 0) {
        const withFrame = sameSize.find(v => v.option2 === (frame || 'Black'));
        if (withFrame) return withFrame;
        return sameSize[0];
      }
    }

    if (frame) {
      const sameFrame = artwork.variants.filter(v => v.option2 === frame && v.available);
      if (sameFrame.length > 0) {
        const withSize = sameFrame.find(v => v.option1 === (size || 'Medium'));
        if (withSize) return withSize;
        return sameFrame[0];
      }
    }
    return artwork.variants.find(v => v.available) || null;
  }


  /**
   * Validate configuration exists for all artworks
   */
  validateConfiguration(artworks, size, frame, mount) {
    return artworks.every(artwork => {
      const variant = this.findVariant(artwork, { size, frame, mount });
      return variant && variant.available;
    });
  }

  /**
   * Calculate total price for configuration
   */
  calculatePrice(artworks, size, frame, mount) {
    const isMember = Alpine.store('membership')?.isLoggedIn;
    const cache_key = `${artworks.map(a => a.id).join('-')}-${size}-${frame}-${mount}-${isMember ? 'member' : 'regular'}`;

    if (this.price_cache.has(cache_key)) {
      return this.price_cache.get(cache_key);
    }

    const total = artworks.reduce((sum, artwork) => {
      const variant = this.findVariant(artwork, { size, frame, mount });
      if (variant) {
        let price;
        if (isMember && variant.compare_at_price) {
          const memberPrice = parseFloat(variant.compare_at_price);
          price = memberPrice > 0 ? memberPrice : parseFloat(variant.price || 0);
        } else {
          price = parseFloat(variant.price || 0);
        }
        return sum + (price * 100);
      }
      return sum;
    }, 0);

    this.price_cache.set(cache_key, total);
    return total;
  }

  /**
   * Calculate member price (for banner display) - always uses member pricing regardless of login status
   */
  calculateMemberPrice(artworks, size, frame, mount) {
    const cache_key = `member-${artworks.map(a => a.id).join('-')}-${size}-${frame}-${mount}`;

    if (this.price_cache.has(cache_key)) {
      return this.price_cache.get(cache_key);
    }

    const total = artworks.reduce((sum, artwork) => {
      const variant = this.findVariant(artwork, { size, frame, mount });

      if (variant) {
        const price = variant.compare_at_price && parseFloat(variant.compare_at_price) > 0 ?
          variant.compare_at_price : variant.price;
        return sum + (parseFloat(price) * 100);
      }
      return sum;
    }, 0);

    this.price_cache.set(cache_key, total);
    return total;
  }

  /**
   * Apply bulk configuration to all artworks
   */
  async applyBulkConfiguration(artworks, config) {
    const { size, frame, mount } = config;

    if (!this.validateConfiguration(artworks, size, frame, mount)) {
      throw new Error('Configuration not available for all artworks');
    }

    const variants = artworks.map(artwork =>
      this.findVariant(artwork, { size, frame, mount })
    );

    await this.preloadVariantImages(variants);

    artworks.forEach((artwork, index) => {
      artwork.selected_variant = variants[index];
      artwork.variant_selected = true;
    });

    this.selected_config = { size, frame, mount };
    return true;
  }

  /**
   * Preload variant images
   */
  async preloadVariantImages(variants) {
    const promises = variants
      .filter(v => v?.featured_image?.src)
      .map(variant => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          const timeout = setTimeout(() => {
            reject(new Error('Image load timeout'));
          }, 5000);

          img.onload = () => {
            clearTimeout(timeout);
            resolve();
          };

          img.onerror = () => {
            clearTimeout(timeout);
            reject(new Error('Image load failed'));
          };

          img.src = variant.featured_image.src;
        });
      });

    try {
      await Promise.all(promises);
    } catch (error) {
      console.warn('Some variant images failed to preload:', error);
    }
  }

  clearCache() {
    this.variant_cache.clear();
    this.price_cache.clear();
  }

  /**
   * Get available options across all artworks
   */
  getAvailableOptions(artworks) {
    if (!artworks || artworks.length === 0) {
      return { sizes: [], frames: [], mounts: [] };
    }

    const sizes = new Set();
    const frames = new Set();
    const mounts = new Set();

    artworks.forEach(artwork => {
      if (artwork && artwork.variants && Array.isArray(artwork.variants)) {
        artwork.variants.forEach(variant => {
          if (variant && variant.option1) sizes.add(variant.option1);
          if (variant && variant.option2) frames.add(variant.option2);
          if (variant && variant.option3) mounts.add(variant.option3);
        });
      }
    });

    return {
      sizes: Array.from(sizes).sort(),
      frames: Array.from(frames).sort(),
      mounts: Array.from(mounts).sort()
    };
  }

  /**
   * Get common options available across ALL selected artworks
   */
  getCommonOptions(artworks) {
    if (!artworks || artworks.length === 0) {
      return { sizes: [], frames: [], mounts: [] };
    }

    const validArtworks = artworks.filter(artwork => artwork && artwork.id && artwork.variants);

    if (validArtworks.length === 0) {
      return { sizes: [], frames: [], mounts: [] };
    }

    if (validArtworks.length === 1) {
      return this.getAvailableOptions(validArtworks);
    }

    const allOptions = validArtworks.map(artwork => this.getAvailableOptions([artwork]));

    const sizes = allOptions.reduce((common, options) =>
      common.filter(size => options.sizes.includes(size)),
      allOptions[0]?.sizes || []
    );

    const frames = allOptions.reduce((common, options) =>
      common.filter(frame => options.frames.includes(frame)),
      allOptions[0]?.frames || []
    );

    const mounts = allOptions.reduce((common, options) =>
      common.filter(mount => options.mounts.includes(mount)),
      allOptions[0]?.mounts || []
    );

    return { sizes, frames, mounts };
  }

  /**
   * Check if a specific option combination is valid for all artworks
   */
  isOptionCombinationValid(artworks, testSelection) {
    if (!artworks || artworks.length === 0) return false;

    const { size, frame, mount } = testSelection;

    if (!size || !frame || !mount) return true;

    return artworks.every(artwork => {
      if (!artwork?.variants) return false;

      return artwork.variants.some(variant =>
        variant.available &&
        variant.option1 === size &&
        variant.option2 === frame &&
        variant.option3 === mount
      );
    });
  }

  /**
   * Check if a specific option value would be valid given current selection
   */
  isOptionSelectable(artworks, optionType, optionValue, currentSelection = {}, implicitSize = null) {
    if (!artworks || artworks.length === 0) return false;

    const testSelection = { ...currentSelection };
    testSelection[optionType] = optionValue;

    const { size, frame, mount } = testSelection;
    const effectiveSize = size || implicitSize;

    if (!effectiveSize || !frame || !mount) return true;

    const testWithEffectiveSize = {
      size: effectiveSize,
      frame,
      mount
    };

    return this.isOptionCombinationValid(artworks, testWithEffectiveSize);
  }

  /**
   * Sort options following existing product template patterns
   */
  sortOptions(options, type) {
    if (type === 'size') {
      const sizeOrder = ['Small', 'Medium', 'Large', 'Giant', 'Collector', 'Exhibition'];
      return options.sort((a, b) => {
        const indexA = sizeOrder.indexOf(a);
        const indexB = sizeOrder.indexOf(b);
        if (indexA === -1 && indexB === -1) return a.localeCompare(b);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
    }

    return options.sort();
  }

  /**
   * Get price range for specific size across artworks
   */
  getPriceRangeForSize(artworks, size) {
      const prices = artworks
        .map(artwork => {
          if (!artwork || !artwork.variants || !Array.isArray(artwork.variants)) {
            return 0;
          }

          const variantsWithSize = artwork.variants.filter(v =>
            v && v.option1 === size && v.available
          );

        if (variantsWithSize.length > 0) {
          return Math.min(...variantsWithSize.map(v => parseFloat(v.price || 0)));
        }
        return 0;
      })
      .filter(price => price > 0);

    if (prices.length === 0) {
      return { min: 0, max: 0, average: 0 };
    }

    return {
      min: Math.min(...prices),
      max: Math.max(...prices),
      average: prices.reduce((sum, price) => sum + price, 0) / prices.length
    };
  }

  getOptionMetadata(type, value) {
    return {};
  }


  /**
   * Find fallback variant with partial configuration
   */
  findFallbackVariant(artwork, partialConfig = {}) {
    if (!artwork?.variants || artwork.variants.length === 0) return null;

    const { frame, mount, requireImage = false } = partialConfig;

    return artwork.variants.find(v =>
      v.available &&
      (!requireImage || v.featured_image) &&
      (!frame || v.option2 === frame) &&
      (!mount || v.option3 === mount)
    ) || null;
  }

  /**
   * Find default variant for artwork
   */
  findDefaultVariant(artwork) {
    if (!artwork?.variants || artwork.variants.length === 0) return null;

    const standard = this._findExactVariant(
      artwork, 'Exhibition', 'Black', 'Border'
    );
    if (standard?.available && standard?.featured_image) {
      return standard;
    }

    return artwork.variants.find(v =>
      v?.available && v?.featured_image
    ) || null;
  }

  /**
   * Find variant by product ID in array of variants
   */
  findPreviewByProductId(variants, productId) {
    if (!variants || !Array.isArray(variants) || !productId) return null;

    const idStr = String(productId);
    return variants.find(v => v?.product_id && String(v.product_id) === idStr) || null;
  }

  /**
   * Find variant by SKU pattern matching
   */
  findVariantBySku(variants, size) {
    if (!variants || !Array.isArray(variants) || !size) return null;

    const exactMatch = variants.find(variant => variant.option1 === size);
    if (exactMatch) return exactMatch;

    const sizeInitial = size.charAt(0);
    return variants.find(variant =>
      variant.sku?.includes(`${sizeInitial}-`) ||
      variant.sku?.includes(`-${sizeInitial}-`)
    ) || null;
  }

  /**
   * Find premium variant configuration
   */
  findPremiumVariant(variants, size) {
    if (!variants || !Array.isArray(variants) || !size) return null;

    return variants.find(variant =>
      variant.option1 === size &&
      variant.option2 === 'Black' &&
      variant.option3 === 'Border' &&
      variant.available
    ) || null;
  }
}